import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt, FaUsers, FaHeart, FaExclamationTriangle } from 'react-icons/fa';

export default function CommunityGuidelines() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-[#1a237e] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FaShieldAlt className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">Règles de la Communauté</h1>
                <p className="mt-1 text-indigo-200">Dernière mise à jour : Janvier 2025</p>
              </div>
            </div>
            <Link
              to="/teacher-chat"
              className="flex items-center text-indigo-200 hover:text-white transition-colors"
            >
              <FaArrowLeft className="w-4 h-4 mr-2" />
              Retour au Chat
            </Link>
          </div>
        </div>
        
        <div className="p-6 space-y-8">
          {/* Introduction */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <FaUsers className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Notre Communauté</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Amalie AI s'engage à créer un environnement d'apprentissage sûr, respectueux et inclusif pour tous nos utilisateurs. 
              Ces règles communautaires définissent les comportements acceptables et inacceptables sur notre plateforme.
            </p>
          </section>

          {/* Acceptable Behavior */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <FaHeart className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Comportements Encouragés</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span><strong>Respect mutuel :</strong> Traitez tous les membres avec courtoisie et respect</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span><strong>Apprentissage constructif :</strong> Posez des questions pertinentes et aidez les autres</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span><strong>Contenu approprié :</strong> Partagez du contenu éducatif et approprié</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span><strong>Diversité :</strong> Célébrez la diversité culturelle et linguistique</span>
              </li>
            </ul>
          </section>

          {/* Prohibited Behavior */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <FaExclamationTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-semibold text-gray-900">Comportements Interdits</h2>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">Haine et Discrimination</h3>
                <p className="text-red-700 text-sm">
                  Tout contenu qui promeut la haine, la discrimination ou l'intolérance basée sur la race, 
                  l'ethnie, la religion, le genre, l'orientation sexuelle ou toute autre caractéristique.
                </p>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h3 className="font-semibold text-orange-800 mb-2">Harcèlement</h3>
                <p className="text-orange-700 text-sm">
                  Intimidation, menaces, harcèlement ou tout comportement visant à nuire à autrui.
                </p>
              </div>

              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                <h3 className="font-semibold text-pink-800 mb-2">Contenu Sexuel</h3>
                <p className="text-pink-700 text-sm">
                  Contenu sexuellement explicite, pornographique ou inapproprié.
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">Violence et Automutilation</h3>
                <p className="text-red-700 text-sm">
                  Contenu violent, encourageant l'automutilation ou présentant des risques pour la sécurité.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Propriété Intellectuelle</h3>
                <p className="text-blue-700 text-sm">
                  Violation des droits d'auteur, utilisation non autorisée de contenu protégé.
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">Vie Privée</h3>
                <p className="text-purple-700 text-sm">
                  Partage d'informations personnelles sans consentement, doxxing.
                </p>
              </div>
            </div>
          </section>

          {/* Reporting Process */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Signalement de Contenu</h2>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">Comment signaler :</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Cliquez sur le bouton "🚩 Signaler ce passage" sous le message concerné</li>
                <li>Sélectionnez la raison du signalement</li>
                <li>Ajoutez des détails supplémentaires si nécessaire</li>
                <li>Soumettez votre signalement</li>
              </ol>
              <p className="text-sm text-gray-600 mt-3">
                Tous les signalements sont examinés par notre équipe de modération dans les 24 heures.
              </p>
            </div>
          </section>

          {/* Consequences */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Conséquences</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Premier avertissement :</strong> Message de rappel des règles
              </p>
              <p>
                <strong>Violations répétées :</strong> Suspension temporaire du compte
              </p>
              <p>
                <strong>Violations graves :</strong> Bannissement permanent de la plateforme
              </p>
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact</h2>
            <p className="text-gray-700">
              Pour toute question concernant ces règles ou pour signaler un problème urgent, 
              contactez notre équipe de modération à : 
              <a href="mailto:moderation@amah.ai" className="text-blue-600 hover:text-blue-800 ml-1">
                moderation@amah.ai
              </a>
            </p>
          </section>

          <div className="pt-6 mt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              En utilisant Amalie AI, vous acceptez de respecter ces règles communautaires.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}